/**
 * usePlanLimits — Hook to check and enforce plan limits across the app
 * 
 * Usage:
 *   const { canAddItem, canUploadImage, checkLimit } = usePlanLimits();
 *   if (!canAddItem()) showLimitModal('items');
 */
import { useState, useCallback } from 'react';
import { PLATFORM_PLANS, PlatformPlanType } from '../types/plans';

type LimitType = 'items' | 'storage' | 'images' | 'workspaces' | 'members' | 'generic';

interface LimitCheckResult {
  allowed: boolean;
  current: number;
  max: number;
  limitType: LimitType;
}

interface UsePlanLimitsReturn {
  currentPlan: PlatformPlanType;
  showLimitModal: boolean;
  limitModalType: LimitType;
  limitCurrent: number;
  limitMax: number;
  closeLimitModal: () => void;
  checkItemLimit: (currentCount: number) => boolean;
  checkImageLimit: (currentImagesOnItem: number) => boolean;
  checkStorageLimit: (usedBytes: number) => boolean;
  checkWorkspaceLimit: (currentCount: number) => boolean;
  checkMemberLimit: (currentCount: number) => boolean;
  checkLimit: (type: LimitType, current: number) => LimitCheckResult;
}

export const usePlanLimits = (planType: PlatformPlanType = 'FREE'): UsePlanLimitsReturn => {
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalType, setLimitModalType] = useState<LimitType>('generic');
  const [limitCurrent, setLimitCurrent] = useState(0);
  const [limitMax, setLimitMax] = useState(0);

  const plan = PLATFORM_PLANS[planType];
  const features = plan.features;

  const openModal = useCallback((type: LimitType, current: number, max: number) => {
    setLimitModalType(type);
    setLimitCurrent(current);
    setLimitMax(max);
    setShowLimitModal(true);
  }, []);

  const closeLimitModal = useCallback(() => {
    setShowLimitModal(false);
  }, []);

  const checkItemLimit = useCallback((currentCount: number): boolean => {
    if (features.maxItems === -1) return true; // unlimited
    if (currentCount >= features.maxItems) {
      openModal('items', currentCount, features.maxItems);
      return false;
    }
    return true;
  }, [features.maxItems, openModal]);

  const checkImageLimit = useCallback((currentImagesOnItem: number): boolean => {
    if (features.maxImagesPerItem === -1) return true;
    if (currentImagesOnItem >= features.maxImagesPerItem) {
      openModal('images', currentImagesOnItem, features.maxImagesPerItem);
      return false;
    }
    return true;
  }, [features.maxImagesPerItem, openModal]);

  const checkStorageLimit = useCallback((usedBytes: number): boolean => {
    const maxBytes = features.storageGB * 1024 * 1024 * 1024;
    if (features.storageGB === -1) return true;
    if (usedBytes >= maxBytes) {
      openModal('storage', Math.round(usedBytes / (1024 * 1024)), Math.round(maxBytes / (1024 * 1024)));
      return false;
    }
    return true;
  }, [features.storageGB, openModal]);

  const checkWorkspaceLimit = useCallback((currentCount: number): boolean => {
    if (features.maxWorkspaces === -1) return true;
    if (currentCount >= features.maxWorkspaces) {
      openModal('workspaces', currentCount, features.maxWorkspaces);
      return false;
    }
    return true;
  }, [features.maxWorkspaces, openModal]);

  const checkMemberLimit = useCallback((currentCount: number): boolean => {
    if (features.maxMembersPerWorkspace === -1) return true;
    if (currentCount >= features.maxMembersPerWorkspace) {
      openModal('members', currentCount, features.maxMembersPerWorkspace);
      return false;
    }
    return true;
  }, [features.maxMembersPerWorkspace, openModal]);

  const checkLimit = useCallback((type: LimitType, current: number): LimitCheckResult => {
    let max = 0;
    switch (type) {
      case 'items': max = features.maxItems; break;
      case 'images': max = features.maxImagesPerItem; break;
      case 'workspaces': max = features.maxWorkspaces; break;
      case 'members': max = features.maxMembersPerWorkspace; break;
      case 'storage': max = Math.round(features.storageGB * 1024); break; // in MB
      default: max = -1;
    }
    const allowed = max === -1 || current < max;
    if (!allowed) openModal(type, current, max);
    return { allowed, current, max, limitType: type };
  }, [features, openModal]);

  return {
    currentPlan: planType,
    showLimitModal,
    limitModalType,
    limitCurrent,
    limitMax,
    closeLimitModal,
    checkItemLimit,
    checkImageLimit,
    checkStorageLimit,
    checkWorkspaceLimit,
    checkMemberLimit,
    checkLimit,
  };
};

export default usePlanLimits;
